import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPencilAlt,
  faPrint,
  faHistory,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import patientService from '../../../../services/patientService';
import practiceService from '../../../../services/practiceService';
import { getRoleDisplayName } from '../../../../utils/roleUtils';
import { getAvailablePlanTypeKeys } from './rpmCarePlanConfig';
import {
  buildPlanEmailText,
  diffPlan,
  formatPlanDate,
  getSummaryRows,
  logUpdateCount,
  printCarePlan,
} from './rpmCarePlanUtils';
import RpmCarePlanModal from './RpmCarePlanModal';
import RpmCarePlanLogModal from './RpmCarePlanLogModal';
import './RpmCarePlan.css';

function providerDisplayName(provider, formatNameLastFirst) {
  const last = String(provider?.last_name || '').trim();
  const first = String(provider?.first_name || '').trim();
  const combined = [last, first].filter(Boolean).join(', ');
  const raw = combined
    || provider?.full_name
    || provider?.name
    || provider?.username
    || '';
  if (formatNameLastFirst) return formatNameLastFirst(raw);
  return raw;
}

function PlanItems({ items, emptyText }) {
  if (!items?.length) return <p className="none">{emptyText}</p>;
  return (
    <ul>
      {items.map((x, i) => (
        <li key={`${x.label}-${i}`}>
          {x.label}
          {x.custom ? <span className="rcp-chk-badge">CUSTOM</span> : null}
        </li>
      ))}
    </ul>
  );
}

const RpmCarePlanTab = ({
  practiceId,
  patientId,
  patient,
  patientFullName,
  getGenderText,
  formatNameLastFirst,
  caregiverDisplayValue,
  latestVitals,
  enabledMonitoring = { bp: true, bg: true, weight: true },
  isReadOnly = false,
}) => {
  const { user } = useSelector((state) => state.auth || {});
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [logPlan, setLogPlan] = useState(null);
  const [practiceProviders, setPracticeProviders] = useState([]);

  const availableTypeKeys = useMemo(
    () => getAvailablePlanTypeKeys(enabledMonitoring),
    [enabledMonitoring?.bp, enabledMonitoring?.bg, enabledMonitoring?.weight]
  );

  const visiblePlans = useMemo(
    () => plans.filter((plan) => availableTypeKeys.includes(plan.typeKey)),
    [plans, availableTypeKeys]
  );

  const sessionUser = useMemo(() => ({
    id: user?.id,
    username: user?.username || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'unknown',
    name: [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || 'Unknown',
    role: user?.role_name || getRoleDisplayName(user?.role_id),
  }), [user]);

  const patientInfo = useMemo(() => {
    const dobRaw = patient?.date_of_birth || patient?.dob || '';
    let age = null;
    if (dobRaw) {
      const birth = new Date(dobRaw);
      if (!Number.isNaN(birth.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birth.getFullYear();
        const md = today.getMonth() - birth.getMonth();
        if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age -= 1;
      }
    }
    const dobMatch = String(dobRaw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dob = dobMatch ? `${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}` : (dobRaw || '—');
    return {
      id: patient?.patients_table_id ?? patient?.id ?? patientId,
      name: patientFullName || `${patient?.last_name || ''}, ${patient?.first_name || ''}`.trim(),
      dob,
      age,
      sex: getGenderText?.(patient?.gender) || '—',
      provider: formatNameLastFirst?.(patient?.provider_name) || patient?.provider_name || '—',
      caregiver: caregiverDisplayValue || '—',
      email: patient?.email || '',
    };
  }, [patient, patientFullName, patientId, getGenderText, formatNameLastFirst, caregiverDisplayValue]);

  const loadPlans = useCallback(async () => {
    if (!practiceId || !patientId) return;
    setLoading(true);
    try {
      const res = await patientService.getRpmCarePlans(practiceId, patientId);
      const data = res?.data?.data ?? res?.data ?? [];
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load RPM care plans:', err);
      toast.error(err?.response?.data?.message || 'Failed to load care plans');
    } finally {
      setLoading(false);
    }
  }, [practiceId, patientId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    practiceService.getPracticeProviders(practiceId, { minimal: true })
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data?.data ?? res?.data ?? [];
        setPracticeProviders(Array.isArray(raw) ? raw : []);
      })
      .catch((err) => {
        console.error('Failed to load practice providers:', err);
      });
    return () => { cancelled = true; };
  }, [practiceId]);

  const providerOptions = useMemo(() => {
    const names = practiceProviders
      .map((p) => providerDisplayName(p, formatNameLastFirst))
      .map((n) => String(n || '').trim())
      .filter((n) => n && n !== '—' && n !== '-');
    return [...new Set(names)];
  }, [practiceProviders, formatNameLastFirst]);

  const openCreate = () => {
    setEditingPlan(null);
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setModalOpen(true);
  };

  const handlePrint = (plan) => {
    const ok = printCarePlan(plan, patientInfo);
    if (!ok) toast.info('Please allow pop-ups to print or share the care plan.');
  };

  const handleEmail = (plan) => {
    const subject = `Care Plan — ${plan.planTitle} — ${patientInfo.name}`;
    const body = buildPlanEmailText(plan, patientInfo);
    const to = patientInfo.email || '';
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSave = async (payload, existing, extra = {}) => {
    if (extra.action === 'print') {
      handlePrint(payload);
      return false;
    }
    if (extra.action === 'email') {
      handleEmail(payload);
      return false;
    }
    setSaving(true);
    try {
      if (existing?.id) {
        const changes = diffPlan(existing, payload);
        const res = await patientService.updateRpmCarePlan(practiceId, patientId, existing.id, {
          ...payload,
          changes,
        });
        const saved = res?.data?.data;
        if (saved) {
          setPlans((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
        } else {
          await loadPlans();
        }
        toast.success('Care plan updated');
      } else {
        const res = await patientService.createRpmCarePlan(practiceId, patientId, payload);
        const saved = res?.data?.data;
        if (saved) {
          setPlans((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
        } else {
          await loadPlans();
        }
        toast.success('Care plan saved');
      }
      setModalOpen(false);
      setEditingPlan(null);
      return true;
    } catch (err) {
      console.error('Failed to save RPM care plan:', err);
      toast.error(err?.response?.data?.message || 'Failed to save care plan');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="patient-details-sub-pane">
      <div className="rcp-tab-header">
        <div>
          <h3>Care Plans — RPM</h3>
        </div>
        {!isReadOnly ? (
          <button type="button" className="rcp-create-btn" onClick={openCreate}>
            <FontAwesomeIcon icon={faPlus} /> Create Care Plan
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="rcp-empty">
          <FontAwesomeIcon icon={faSpinner} spin /> Loading care plans…
        </div>
      ) : visiblePlans.length === 0 ? (
        <div className="rcp-empty">
          {isReadOnly
            ? 'No care plans are available yet.'
            : 'No care plans yet. Create one to set goals, monitoring, and education for this patient.'}
        </div>
      ) : (
        visiblePlans.map((plan) => {
          const approved = plan.signoff?.approvalStatus === 'approved' || plan.approvalStatus === 'approved';
          const updates = logUpdateCount(plan);
          return (
            <div key={plan.id} className="rcp-plan">
              <div className="rcp-plan-top">
                <div className="rcp-plan-title">
                  {plan.planTitle}
                  <span className={`rcp-pill ${approved ? 'approved' : 'pending'}`}>
                    {approved ? 'Approved' : 'Pending approval'}
                  </span>
                </div>
                {!isReadOnly ? (
                  <div className="rcp-plan-actions">
                    <button type="button" className="rcp-plan-btn" onClick={() => openEdit(plan)}>
                      <FontAwesomeIcon icon={faPencilAlt} /> Edit
                    </button>
                    <button type="button" className="rcp-plan-btn" onClick={() => setLogPlan(plan)}>
                      <FontAwesomeIcon icon={faHistory} /> Edit logs{updates ? ` (${updates})` : ''}
                    </button>
                    <button type="button" className="rcp-plan-btn print" onClick={() => handlePrint(plan)}>
                      <FontAwesomeIcon icon={faPrint} /> Print / share
                    </button>
                  </div>
                ) : null}
                <div className="rcp-plan-date">
                  Created {formatPlanDate(plan.signoff?.createdDate || plan.created_at)}
                  {plan.edited ? ` · Edited ${formatPlanDate(plan.updated_at)}` : ''}
                </div>
              </div>
              <div className="rcp-plan-meta">
                <span>Created by <b>{plan.signoff?.createdBy || '—'}</b></span>
                <span>Approved by <b>{plan.signoff?.approvedBy || '—'}</b></span>
              </div>
              <div className="rcp-plan-grid">
                {getSummaryRows(plan).map((row) => (
                  <div key={row[0]}>
                    <span>{row[0]}</span>
                    <b>{row[1] || '—'}</b>
                  </div>
                ))}
              </div>
              <div className="rcp-plan-lists">
                <div className="rcp-plan-list">
                  <div className="rcp-plan-list-h">Interventions ordered ({plan.interventions?.length || 0})</div>
                  <PlanItems items={plan.interventions} emptyText="None ordered" />
                </div>
                <div className="rcp-plan-list">
                  <div className="rcp-plan-list-h">Education &amp; teach-back ({plan.education?.length || 0})</div>
                  <PlanItems items={plan.education} emptyText="None selected" />
                </div>
              </div>
            </div>
          );
        })
      )}

      <RpmCarePlanModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingPlan(null); }}
        onSave={handleSave}
        saving={saving}
        editingPlan={editingPlan}
        patientInfo={patientInfo}
        sessionUser={sessionUser}
        providerName={patientInfo.provider !== '—' ? patientInfo.provider : ''}
        providerOptions={providerOptions}
        latestVitals={latestVitals}
        availableTypeKeys={availableTypeKeys}
      />

      <RpmCarePlanLogModal
        isOpen={Boolean(logPlan)}
        onClose={() => setLogPlan(null)}
        plan={logPlan}
        patientName={patientInfo.name}
      />
    </div>
  );
};

export default RpmCarePlanTab;
